import { toUsStateCode } from "../../../services/helpers/usStateCodes";

export type IncentiveRequirementsVectorFields = {
  requiredState: string | null;
  mustBeInNyc: boolean | null;
  eligibleZipCodes: string[];
  eligiblePropertyTypeCategories: string[];
  eligiblePropertyTypeDetails: string[];
  excludedPropertyTypeCategories: string[];
  excludedPropertyTypeDetails: string[];
  eligibleBuildingUses: string[];
  excludedBuildingUses: string[];
  minimumUnits: number | null;
  maximumUnits: number | null;
  minimumSqFt: number | null;
  maximumSqFt: number | null;
  minimumCapacityMw: number | null;
  utilityTerritories: string[];
  requiredElectricProvider: string | null;
  requiredGasProvider: string | null;
  eligibleApplicantClasses: string[];
  excludedApplicantClasses: string[];
  assetTypology: string[];
  requiresAffordableHousing: boolean | null;
  requiresHistoric: boolean | null;
  requiresHomeownership: boolean | null;
  requiresBarn: boolean | null;
  requiresIncomeQualification: boolean | null;
  incentiveStackingAllowed: boolean | null;
  underservedCommunitiesPriority: boolean | null;
  screeningProgramIds: string[];
  eligibleProjectTypes: string[];
  eligibleTechnologies: string[];
};

export type IncentiveRequirementsVectorIdentity = {
  programId?: number | null;
  incentiveId?: number | null;
  programName?: string | null;
  stateCode?: string | null;
};

export const UTILITY_PROVIDER_OPTIONS = [
  "Con Edison",
  "National Grid",
  "National Fuel Gas",
  "NYSEG",
  "RG&E",
  "Orange & Rockland",
  "Central Hudson",
  "PSE&G",
  "JCP&L",
] as const;

/**
 * Applicant classes come from the shared vocabulary, not from a list local to
 * the incentive editor. Two lists is how `Homeowner` here and `homeowner` on
 * the property side drifted apart — see IMPL-002 R2.
 */
export { AUTHORABLE_APPLICANT_CLASS_IDS as APPLICANT_CLASS_OPTIONS } from "../../../services/incentiveMatchingVocabulary";

const SCHEMA_VERSION = 1;

const CONTRACT_KEY_ORDER = [
  "schemaVersion",
  "programId",
  "incentiveId",
  "programName",
  "screeningProgramIds",
  "requiredState",
  "mustBeInNyc",
  "eligibleZipCodes",
  "eligiblePropertyTypeCategories",
  "eligiblePropertyTypeDetails",
  "excludedPropertyTypeCategories",
  "excludedPropertyTypeDetails",
  "eligibleBuildingUses",
  "excludedBuildingUses",
  "minimumUnits",
  "maximumUnits",
  "minimumSqFt",
  "maximumSqFt",
  "minimumCapacityMw",
  "utilityTerritories",
  "requiredElectricProvider",
  "requiredGasProvider",
  "eligibleApplicantClasses",
  "excludedApplicantClasses",
  "assetTypology",
  "requiresAffordableHousing",
  "requiresHistoric",
  "requiresHomeownership",
  "requiresBarn",
  "requiresIncomeQualification",
  "incentiveStackingAllowed",
  "underservedCommunitiesPriority",
  "eligibleProjectTypes",
  "eligibleTechnologies",
] as const;

const REQUIRED_STATE_KEYS = ["requiredState", "RequiredState"] as const;

const LIST_KEY_ALIASES = {
  screeningProgramIds: ["screeningProgramIds", "ScreeningProgramIds"],
  eligibleZipCodes: ["eligibleZipCodes", "EligibleZipCodes"],
  eligiblePropertyTypeCategories: [
    "eligiblePropertyTypeCategories",
    "EligiblePropertyTypeCategories",
  ],
  eligiblePropertyTypeDetails: [
    "eligiblePropertyTypeDetails",
    "EligiblePropertyTypeDetails",
  ],
  excludedPropertyTypeCategories: [
    "excludedPropertyTypeCategories",
    "ExcludedPropertyTypeCategories",
  ],
  excludedPropertyTypeDetails: [
    "excludedPropertyTypeDetails",
    "ExcludedPropertyTypeDetails",
  ],
  eligibleBuildingUses: ["eligibleBuildingUses", "EligibleBuildingUses"],
  excludedBuildingUses: ["excludedBuildingUses", "ExcludedBuildingUses"],
  utilityTerritories: ["utilityTerritories", "UtilityTerritories"],
  eligibleApplicantClasses: [
    "eligibleApplicantClasses",
    "EligibleApplicantClasses",
  ],
  excludedApplicantClasses: [
    "excludedApplicantClasses",
    "ExcludedApplicantClasses",
  ],
  assetTypology: ["assetTypology", "AssetTypology"],
  eligibleProjectTypes: ["eligibleProjectTypes", "EligibleProjectTypes"],
  eligibleTechnologies: ["eligibleTechnologies", "EligibleTechnologies"],
} as const;

const BOOL_KEY_ALIASES = {
  mustBeInNyc: ["mustBeInNyc", "mustBeInNYC", "MustBeInNyc"],
  requiresAffordableHousing: [
    "requiresAffordableHousing",
    "RequiresAffordableHousing",
  ],
  requiresHistoric: ["requiresHistoric", "RequiresHistoric"],
  requiresHomeownership: [
    "requiresHomeownership",
    "RequiresHomeownership",
    "requiresPrimaryResidence",
  ],
  requiresBarn: ["requiresBarn", "RequiresBarn"],
  requiresIncomeQualification: [
    "requiresIncomeQualification",
    "RequiresIncomeQualification",
  ],
  incentiveStackingAllowed: [
    "incentiveStackingAllowed",
    "IncentiveStackingAllowed",
  ],
  underservedCommunitiesPriority: [
    "underservedCommunitiesPriority",
    "UnderservedCommunitiesPriority",
  ],
} as const;

const INT_KEY_ALIASES = {
  minimumUnits: ["minimumUnits", "MinimumUnits"],
  maximumUnits: ["maximumUnits", "MaximumUnits"],
  minimumSqFt: ["minimumSqFt", "MinimumSqFt", "MinimumSqft"],
  maximumSqFt: ["maximumSqFt", "MaximumSqFt", "MaximumSqft"],
} as const;

const DECIMAL_KEY_ALIASES = {
  minimumCapacityMw: [
    "minimumCapacityMw",
    "MinimumCapacityMw",
    "minimumCapacityMWAC",
  ],
} as const;

const STRING_KEY_ALIASES = {
  requiredElectricProvider: [
    "requiredElectricProvider",
    "ElectricServiceProvider",
    "RequiredElectricServiceProvider",
  ],
  requiredGasProvider: [
    "requiredGasProvider",
    "GasServiceProvider",
    "RequiredGasServiceProvider",
  ],
} as const;

export function emptyIncentiveRequirementsFields(): IncentiveRequirementsVectorFields {
  return {
    requiredState: null,
    mustBeInNyc: null,
    eligibleZipCodes: [],
    eligiblePropertyTypeCategories: [],
    eligiblePropertyTypeDetails: [],
    excludedPropertyTypeCategories: [],
    excludedPropertyTypeDetails: [],
    eligibleBuildingUses: [],
    excludedBuildingUses: [],
    minimumUnits: null,
    maximumUnits: null,
    minimumSqFt: null,
    maximumSqFt: null,
    minimumCapacityMw: null,
    utilityTerritories: [],
    requiredElectricProvider: null,
    requiredGasProvider: null,
    eligibleApplicantClasses: [],
    excludedApplicantClasses: [],
    assetTypology: [],
    requiresAffordableHousing: null,
    requiresHistoric: null,
    requiresHomeownership: null,
    requiresBarn: null,
    requiresIncomeQualification: null,
    incentiveStackingAllowed: null,
    underservedCommunitiesPriority: null,
    screeningProgramIds: [],
    eligibleProjectTypes: [],
    eligibleTechnologies: [],
  };
}

export function parseIncentiveRequirementsFields(
  json: string | null | undefined
): IncentiveRequirementsVectorFields {
  const root = parseObject(json);
  if (!root) {
    return emptyIncentiveRequirementsFields();
  }

  return {
    requiredState: readRequiredState(root),
    mustBeInNyc: readBool(root, BOOL_KEY_ALIASES.mustBeInNyc),
    eligibleZipCodes: readRootStringList(root, LIST_KEY_ALIASES.eligibleZipCodes),
    eligiblePropertyTypeCategories: readRootStringList(
      root,
      LIST_KEY_ALIASES.eligiblePropertyTypeCategories
    ),
    eligiblePropertyTypeDetails: readRootStringList(
      root,
      LIST_KEY_ALIASES.eligiblePropertyTypeDetails
    ),
    excludedPropertyTypeCategories: readRootStringList(
      root,
      LIST_KEY_ALIASES.excludedPropertyTypeCategories
    ),
    excludedPropertyTypeDetails: readRootStringList(
      root,
      LIST_KEY_ALIASES.excludedPropertyTypeDetails
    ),
    eligibleBuildingUses: readRootStringList(
      root,
      LIST_KEY_ALIASES.eligibleBuildingUses
    ),
    excludedBuildingUses: readRootStringList(
      root,
      LIST_KEY_ALIASES.excludedBuildingUses
    ),
    minimumUnits: readInt(root, INT_KEY_ALIASES.minimumUnits),
    maximumUnits: readInt(root, INT_KEY_ALIASES.maximumUnits),
    minimumSqFt: readInt(root, INT_KEY_ALIASES.minimumSqFt),
    maximumSqFt: readInt(root, INT_KEY_ALIASES.maximumSqFt),
    minimumCapacityMw: readDecimal(root, DECIMAL_KEY_ALIASES.minimumCapacityMw),
    utilityTerritories: readRootStringList(
      root,
      LIST_KEY_ALIASES.utilityTerritories
    ),
    requiredElectricProvider: readString(
      root,
      STRING_KEY_ALIASES.requiredElectricProvider
    ),
    requiredGasProvider: readString(
      root,
      STRING_KEY_ALIASES.requiredGasProvider
    ),
    eligibleApplicantClasses: readRootStringList(
      root,
      LIST_KEY_ALIASES.eligibleApplicantClasses
    ),
    excludedApplicantClasses: readRootStringList(
      root,
      LIST_KEY_ALIASES.excludedApplicantClasses
    ),
    assetTypology: readRootStringList(root, LIST_KEY_ALIASES.assetTypology),
    requiresAffordableHousing: readBool(
      root,
      BOOL_KEY_ALIASES.requiresAffordableHousing
    ),
    requiresHistoric: readBool(root, BOOL_KEY_ALIASES.requiresHistoric),
    requiresHomeownership: readBool(
      root,
      BOOL_KEY_ALIASES.requiresHomeownership
    ),
    requiresBarn: readBool(root, BOOL_KEY_ALIASES.requiresBarn),
    requiresIncomeQualification: readBool(
      root,
      BOOL_KEY_ALIASES.requiresIncomeQualification
    ),
    incentiveStackingAllowed: readBool(
      root,
      BOOL_KEY_ALIASES.incentiveStackingAllowed
    ),
    underservedCommunitiesPriority: readBool(
      root,
      BOOL_KEY_ALIASES.underservedCommunitiesPriority
    ),
    screeningProgramIds: readRootStringList(
      root,
      LIST_KEY_ALIASES.screeningProgramIds
    ),
    eligibleProjectTypes: readRootStringList(
      root,
      LIST_KEY_ALIASES.eligibleProjectTypes
    ),
    eligibleTechnologies: readRootStringList(
      root,
      LIST_KEY_ALIASES.eligibleTechnologies
    ),
  };
}

export function mergeIncentiveRequirementsFields(
  existingJson: string | null | undefined,
  fields: IncentiveRequirementsVectorFields,
  identity?: IncentiveRequirementsVectorIdentity
): string {
  const root = parseObject(existingJson) ?? {};

  applyContractFields(root, fields);
  applyIdentity(root, identity);
  stripEligibilityCriteria(root);
  setIgnoreCase(root, "schemaVersion", SCHEMA_VERSION);

  return JSON.stringify(orderContractRoot(root));
}

export function buildIncentiveRequirementsSkeleton(
  identity?: IncentiveRequirementsVectorIdentity
): string {
  return mergeIncentiveRequirementsFields(
    "{}",
    emptyIncentiveRequirementsFields(),
    identity
  );
}

export function mergeIncentiveRequirementsIdentity(
  existingJson: string | null | undefined,
  identity: IncentiveRequirementsVectorIdentity
): string {
  if (existingJson != null && existingJson.trim()) {
    const parsed = parseObject(existingJson);
    if (!parsed) {
      return existingJson;
    }
    applyIdentity(parsed, identity);
    stripEligibilityCriteria(parsed);
    setIgnoreCase(parsed, "schemaVersion", SCHEMA_VERSION);
    return JSON.stringify(orderContractRoot(parsed));
  }

  return buildIncentiveRequirementsSkeleton(identity);
}

function applyContractFields(
  root: Record<string, unknown>,
  fields: IncentiveRequirementsVectorFields
): void {
  applyRequiredState(root, fields.requiredState);
  applyBool(root, BOOL_KEY_ALIASES.mustBeInNyc, fields.mustBeInNyc);
  applyStringList(
    root,
    LIST_KEY_ALIASES.eligibleZipCodes,
    fields.eligibleZipCodes
  );
  applyStringList(
    root,
    LIST_KEY_ALIASES.eligiblePropertyTypeCategories,
    fields.eligiblePropertyTypeCategories
  );
  applyStringList(
    root,
    LIST_KEY_ALIASES.eligiblePropertyTypeDetails,
    fields.eligiblePropertyTypeDetails
  );
  applyStringList(
    root,
    LIST_KEY_ALIASES.excludedPropertyTypeCategories,
    fields.excludedPropertyTypeCategories
  );
  applyStringList(
    root,
    LIST_KEY_ALIASES.excludedPropertyTypeDetails,
    fields.excludedPropertyTypeDetails
  );
  applyStringList(
    root,
    LIST_KEY_ALIASES.eligibleBuildingUses,
    fields.eligibleBuildingUses
  );
  applyStringList(
    root,
    LIST_KEY_ALIASES.excludedBuildingUses,
    fields.excludedBuildingUses
  );
  applyNumber(root, INT_KEY_ALIASES.minimumUnits, fields.minimumUnits);
  applyNumber(root, INT_KEY_ALIASES.maximumUnits, fields.maximumUnits);
  applyNumber(root, INT_KEY_ALIASES.minimumSqFt, fields.minimumSqFt);
  applyNumber(root, INT_KEY_ALIASES.maximumSqFt, fields.maximumSqFt);
  applyNumber(
    root,
    DECIMAL_KEY_ALIASES.minimumCapacityMw,
    fields.minimumCapacityMw
  );
  applyStringList(
    root,
    LIST_KEY_ALIASES.utilityTerritories,
    fields.utilityTerritories
  );
  applyNullableString(
    root,
    STRING_KEY_ALIASES.requiredElectricProvider,
    fields.requiredElectricProvider
  );
  applyNullableString(
    root,
    STRING_KEY_ALIASES.requiredGasProvider,
    fields.requiredGasProvider
  );
  applyStringList(
    root,
    LIST_KEY_ALIASES.eligibleApplicantClasses,
    fields.eligibleApplicantClasses
  );
  applyStringList(
    root,
    LIST_KEY_ALIASES.excludedApplicantClasses,
    fields.excludedApplicantClasses
  );
  applyStringList(root, LIST_KEY_ALIASES.assetTypology, fields.assetTypology);
  applyBool(
    root,
    BOOL_KEY_ALIASES.requiresAffordableHousing,
    fields.requiresAffordableHousing
  );
  applyBool(root, BOOL_KEY_ALIASES.requiresHistoric, fields.requiresHistoric);
  applyBool(
    root,
    BOOL_KEY_ALIASES.requiresHomeownership,
    fields.requiresHomeownership
  );
  applyBool(root, BOOL_KEY_ALIASES.requiresBarn, fields.requiresBarn);
  applyBool(
    root,
    BOOL_KEY_ALIASES.requiresIncomeQualification,
    fields.requiresIncomeQualification
  );
  applyBool(
    root,
    BOOL_KEY_ALIASES.incentiveStackingAllowed,
    fields.incentiveStackingAllowed
  );
  applyBool(
    root,
    BOOL_KEY_ALIASES.underservedCommunitiesPriority,
    fields.underservedCommunitiesPriority
  );
  applyStringList(
    root,
    LIST_KEY_ALIASES.screeningProgramIds,
    fields.screeningProgramIds
  );
  applyStringList(
    root,
    LIST_KEY_ALIASES.eligibleProjectTypes,
    fields.eligibleProjectTypes
  );
  applyStringList(
    root,
    LIST_KEY_ALIASES.eligibleTechnologies,
    fields.eligibleTechnologies
  );
}

function orderContractRoot(
  root: Record<string, unknown>
): Record<string, unknown> {
  const ordered: Record<string, unknown> = {};
  const used = new Set<string>();

  for (const key of CONTRACT_KEY_ORDER) {
    if (Object.prototype.hasOwnProperty.call(root, key)) {
      ordered[key] = root[key];
      used.add(key);
      continue;
    }
    const existingName = Object.keys(root).find(
      (name) => name.toLowerCase() === key.toLowerCase()
    );
    if (existingName) {
      ordered[key] = root[existingName];
      used.add(existingName);
    }
  }

  for (const [name, value] of Object.entries(root)) {
    if (used.has(name)) {
      continue;
    }
    if (name.toLowerCase() === "eligibilitycriteria") {
      continue;
    }
    ordered[name] = value;
  }

  return ordered;
}

function stripEligibilityCriteria(root: Record<string, unknown>): void {
  deleteIgnoreCase(root, ["eligibilityCriteria", "EligibilityCriteria"]);
}

function parseObject(
  json: string | null | undefined
): Record<string, unknown> | null {
  if (json == null || !json.trim()) {
    return null;
  }

  try {
    const parsed: unknown = JSON.parse(json);
    return asRecord(parsed);
  } catch {
    return null;
  }
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (value == null || typeof value !== "object" || Array.isArray(value)) {
    return null;
  }
  return value as Record<string, unknown>;
}

function getIgnoreCase(
  record: Record<string, unknown>,
  keys: readonly string[]
): unknown {
  const lookup = new Set(keys.map((key) => key.toLowerCase()));
  for (const [name, value] of Object.entries(record)) {
    if (lookup.has(name.toLowerCase())) {
      return value;
    }
  }
  return undefined;
}

function setIgnoreCase(
  record: Record<string, unknown>,
  canonicalKey: string,
  value: unknown
): void {
  for (const name of Object.keys(record)) {
    if (name.toLowerCase() === canonicalKey.toLowerCase() && name !== canonicalKey) {
      delete record[name];
    }
  }
  record[canonicalKey] = value;
}

function deleteIgnoreCase(
  record: Record<string, unknown>,
  keys: readonly string[]
): void {
  const lookup = new Set(keys.map((key) => key.toLowerCase()));
  for (const name of Object.keys(record)) {
    if (lookup.has(name.toLowerCase())) {
      delete record[name];
    }
  }
}

function readRequiredState(record: Record<string, unknown>): string | null {
  return readString(record, REQUIRED_STATE_KEYS);
}

function readRootStringList(
  root: Record<string, unknown>,
  keys: readonly string[]
): string[] {
  return asStringList(getIgnoreCase(root, keys));
}

function readString(
  record: Record<string, unknown>,
  keys: readonly string[]
): string | null {
  const value = getIgnoreCase(record, keys);
  if (value == null) {
    return null;
  }
  if (typeof value !== "string") {
    return null;
  }
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
}

function readBool(
  record: Record<string, unknown>,
  keys: readonly string[]
): boolean | null {
  const value = getIgnoreCase(record, keys);
  if (value === true || value === false) {
    return value;
  }
  if (typeof value === "string") {
    if (value.trim().toLowerCase() === "true") {
      return true;
    }
    if (value.trim().toLowerCase() === "false") {
      return false;
    }
  }
  return null;
}

function readInt(
  record: Record<string, unknown>,
  keys: readonly string[]
): number | null {
  const value = getIgnoreCase(record, keys);
  if (typeof value === "number" && Number.isFinite(value)) {
    return Math.trunc(value);
  }
  if (typeof value === "string" && value.trim()) {
    const parsed = Number.parseInt(value.trim(), 10);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function readDecimal(
  record: Record<string, unknown>,
  keys: readonly string[]
): number | null {
  const value = getIgnoreCase(record, keys);
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === "string" && value.trim()) {
    const parsed = Number.parseFloat(value.trim());
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function asStringList(value: unknown): string[] {
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed ? [trimmed] : [];
  }
  if (!Array.isArray(value)) {
    return [];
  }

  const seen = new Set<string>();
  const result: string[] = [];
  for (const item of value) {
    if (typeof item !== "string") {
      continue;
    }
    const trimmed = item.trim();
    if (!trimmed) {
      continue;
    }
    const key = trimmed.toLowerCase();
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);
    result.push(trimmed);
  }
  return result;
}

function applyRequiredState(
  root: Record<string, unknown>,
  requiredState: string | null
): void {
  const normalized = requiredState?.trim()
    ? toUsStateCode(requiredState) ?? requiredState.trim()
    : null;
  setIgnoreCase(root, "requiredState", normalized);
}

function applyStringList(
  root: Record<string, unknown>,
  keys: readonly string[],
  values: string[]
): void {
  deleteIgnoreCase(root, keys);
  root[keys[0]] = asStringList(values);
}

function applyNullableString(
  root: Record<string, unknown>,
  keys: readonly string[],
  value: string | null
): void {
  deleteIgnoreCase(root, keys);
  const trimmed = value?.trim() ? value.trim() : null;
  root[keys[0]] = trimmed;
}

function applyBool(
  root: Record<string, unknown>,
  keys: readonly string[],
  value: boolean | null
): void {
  deleteIgnoreCase(root, keys);
  root[keys[0]] = value;
}

function applyNumber(
  root: Record<string, unknown>,
  keys: readonly string[],
  value: number | null
): void {
  deleteIgnoreCase(root, keys);
  root[keys[0]] =
    value == null || !Number.isFinite(value) ? null : value;
}

function applyIdentity(
  root: Record<string, unknown>,
  identity?: IncentiveRequirementsVectorIdentity
): void {
  if (!identity) {
    return;
  }
  if (identity.programId != null) {
    setIgnoreCase(root, "programId", identity.programId);
  }
  if (identity.incentiveId != null) {
    setIgnoreCase(root, "incentiveId", identity.incentiveId);
  }
  if (identity.programName != null && identity.programName.trim()) {
    setIgnoreCase(root, "programName", identity.programName.trim());
  }
  if (Object.prototype.hasOwnProperty.call(identity, "stateCode")) {
    applyRequiredState(root, identity.stateCode ?? null);
  }
}
